import {
  // This is fine because this file is for actions.
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  readState,
  writePromise,
} from "@/__main__/app-state.mjs";
import { isPersistent, isVolatile } from "@/__main__/constants.mjs";
import getData, { readData } from "@/__main__/get-data.mjs";
import { setVolatileKV, writeSettings } from "@/app/actions.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { getRitoLanguageCodeFromBCP47 } from "@/app/util.mjs";
import * as API from "@/game-lol/apis/api.mjs";
import {
  EVENT_LOL_FRIENDS,
  QUEUE_SYMBOLS,
  RANK_SYMBOL_TO_STR,
  ROLE_SYMBOL_TO_STR,
  SERVICES_TO_REGIONS,
} from "@/game-lol/constants/constants.mjs";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import inGameState from "@/game-lol/in-game-state.mjs";
import LoLSummoner from "@/game-lol/models/lol-summoner.mjs";
import getCurrentSummoner from "@/game-lol/utils/get-current-summoner.mjs";
import QueueSymbol, {
  QUEUE_SYMBOL_TO_OBJECT,
} from "@/game-lol/utils/symbol-queue.mjs";
import { RankSymbol, RankSymbolArena } from "@/game-lol/utils/symbol-rank.mjs";
import RoleSymbol from "@/game-lol/utils/symbol-role.mjs";
import {
  extractRegionFromDerivedId,
  getCurrentSeason,
  getDerivedId,
} from "@/game-lol/utils/util.mjs";
import { TFT_RANKED_QUEUES } from "@/game-tft/constants.mjs";
import { writeOverlaySetting } from "@/library/actions.mjs";
import { deepSearch } from "@/util//deep-search.mjs";
import clone from "@/util/clone.mjs";
import deepMerge from "@/util/deep-merge.mjs";
import { appLog } from "@/util/dev.mjs";
import { getLocale } from "@/util/i18n-helper.mjs";

export function updateLoggedInSummonerSettings(region, summoner) {
  if (!summoner || !region) return;
  const { puuid } = summoner;
  if (!puuid) return;
  writeSettings(["lastLoggedInIdByGame", "lol"], puuid);
  writeSettings(["loggedInAccounts", "lol", puuid], {
    region,
    ...summoner,
  });

  const { gameName, tagLine } = summoner;
  const derivedId = getDerivedId(region, null, gameName, tagLine);
  setVolatileKV("currentSummoner", derivedId);
  setVolatileKV("currentRegion", region);

  if (!SERVICES_TO_REGIONS[region.toLowerCase()]) return;

  writeState.lol.lcuProfiles[derivedId] = {
    ...summoner,
    [isVolatile]: true,
  };
}

export function updateCurrentPatch(data = null) {
  // if still null: game is still patching
  if (data === null) return;
  writeState.lol.currentPatch = {
    major: parseInt(data.split(".")?.[0], 10),
    minor: parseInt(data.split(".")?.[1], 10),
    short: data.split(".").slice(0, 2).join("."),
    detailed: data.match(/(\d+\.\d+\.\d+)/)?.[0],
    full: data,
    [isVolatile]: true,
  };
  inGameState.currentPatch = data.split(".").slice(0, 2).join(".");
}

export function removeCurrentSummoner() {
  setVolatileKV("currentSummoner", null);
}

export function updateRuneDetails(patch, runeDetails) {
  const lang = getLocale();
  const ritoLanguageCode = getRitoLanguageCodeFromBCP47(lang);
  runeDetails[isVolatile] = true;
  writeState.lol.staticData[ritoLanguageCode][patch].runeDetails = runeDetails;
}

export function updateAvailableQueues(queues) {
  const availableQueues = queues
    .filter(
      (queue) => queue.queueAvailability === "Available" && queue.isVisible,
    )
    .map(({ name, description, id, gameMode, type, mapId, isVisible }) => {
      return {
        name,
        description,
        id,
        gameMode,
        type,
        mapId,
        isVisible,
      };
    });

  availableQueues[isVolatile] = true;
  writeState.lol.queues = availableQueues;
}

export function readFriendsList(friends) {
  eventBus.emit(EVENT_LOL_FRIENDS, {
    me: inGameState.localSummoner.puuid,
    friends: friends.map((friend) => friend.puuid),
  });
}

export function loadLocalMatchlist() {
  return readData(["lol", "localMatchlists"]);
}

export function updateLatestChampionsPatch(data) {
  const patch = data?.[0]?.patch;
  if (!patch) return;
  writeState.lol.championStatsPatch = patch;
}

export function updateLatestSynergiesPatch(data) {
  const patch = data?.[0]?.patch;
  if (!patch) return;
  writeState.lol.championSynergiesPatch = patch;
}

export function updateLatestMatchupsPatch(data) {
  const patch = data?.[0]?.patch;
  if (!patch) return;
  writeState.lol.championMatchupsPatch = patch;
}

export function updateLocalMatchlist(gameId, playerMatchStats = {}) {
  const [region, name] = getCurrentSummoner();
  const derivedId = getDerivedId(region, name);

  const matchlistGameId = `${region.toUpperCase()}_${gameId}`;
  const allMatchlists = clone(readState.lol.localMatchlists) || {};
  const matchlist = allMatchlists[derivedId] || [];
  const existingMatch = matchlist.find((match) => match.id === matchlistGameId);

  const deltaLp =
    playerMatchStats?.leaguePointsDelta ??
    existingMatch?.playerMatch?.playerMatchStats?.deltaLp ??
    null;
  const lp =
    playerMatchStats?.leaguePoints ??
    existingMatch?.playerMatch?.playerMatchStats?.lp ??
    null;

  const matchlistItem = {
    id: matchlistGameId,
    playerMatch: {
      id: null,
      playerMatchStats: {
        deltaLp,
        lp,
      },
    },
  };

  const matchlistIndex = matchlist.indexOf(existingMatch);

  let updatedMatchlist;
  if (matchlistIndex < 0) {
    appLog(
      "[LoL] [INSERT MATCH]",
      `Match not found in exist matchlist, inserting ${matchlistGameId} at the top`,
    );
    updatedMatchlist = [matchlistItem, ...matchlist];
  } else {
    appLog(
      "[LoL] [INSERT MATCH]",
      `Match already exists in match list, ${matchlistGameId}`,
    );
    updatedMatchlist = [...matchlist];
    updatedMatchlist[matchlistIndex] = matchlistItem;
  }

  allMatchlists[derivedId] = updatedMatchlist;
  allMatchlists[isPersistent] = Date.now() + 1000 * 60 * 60 * 24 * 7;
  writeState.lol.localMatchlists = allMatchlists;
  return writePromise();
}

export function removeMatchfromLocalMatchlist(region, gameId) {
  const matchlistGameId = `${region.toUpperCase()}_${gameId}`;
  const allMatchlists = clone(readState.lol.localMatchlists) || {};
  const derivedId = Object.keys(allMatchlists).find((derivedId) => {
    const matchlist = allMatchlists[derivedId];
    if (Array.isArray(matchlist)) {
      return matchlist.some((match) => match.id === matchlistGameId);
    }
    return false;
  });
  if (!derivedId) return;
  const matchlist = allMatchlists[derivedId] || [];
  const updatedMatchlist = matchlist.filter(
    (match) => match.id !== matchlistGameId,
  );
  appLog(
    `[LoL] Removed match ${matchlistGameId}, new local matchlist for ${derivedId} is now ${JSON.stringify(updatedMatchlist.map((m) => m.id))}`,
  );

  allMatchlists[derivedId] = updatedMatchlist;
  allMatchlists[isPersistent] = Date.now() + 1000 * 60 * 60 * 24 * 7;
  writeState.lol.localMatchlists = allMatchlists;
  return writePromise();
}

export function updateLobbyMembers(region, lobby) {
  const othersInLobby = lobby.members
    .filter((member) => member.puuid !== lobby.localMember.puuid)
    .map((member) => getDerivedId(region, member.summonerName));

  othersInLobby[isVolatile] = true;
  writeState.lol.lobbyMembers = othersInLobby;
}

export function updateLoLState(state) {
  setVolatileKV("lolGameState", {
    ...(readState.volatile.lolGameState ?? {}),
    ...state,
  });
}

export function resetLoLState() {
  setVolatileKV("lolGameState", {});
  setVolatileKV("LoLLoadingState", {});
}

export async function getLolGameState() {
  const writeStatePath = ["lolGameState"];
  const result = await readData(writeStatePath);
  return result;
}

export function updateSummonerRankedStats(data) {
  const rankedStats = Object.values(deepSearch(data, "queueMap"));
  if (!rankedStats) return;

  /* IMPORTANT!!! - DONT USE ANYTHING EXCEPT `volatile.currentSummoner` HERE */
  const derivedId = readState.volatile.currentSummoner;
  if (!derivedId) return;

  const region = extractRegionFromDerivedId(derivedId);
  const account = readState.lol.profiles[derivedId];

  if (!account || region === "pbe1") return;

  const leagueRankedStats = rankedStats.filter(
    ({ queueType }) => !TFT_RANKED_QUEUES.includes(queueType),
  );

  const mappedRanks = leagueRankedStats.map((rank) => {
    const queueSymbol = QueueSymbol(rank.queueType);
    const isArena = queueSymbol === QUEUE_SYMBOLS.arena;
    return {
      accountId: rank?.accountId || account.accountId,
      id: account.summonerId,
      insertedAt: new Date(),
      timestamp: Date.now(),
      queue: queueSymbol,
      queueType: QUEUE_SYMBOL_TO_OBJECT[queueSymbol].gql,
      tier: isArena ? RankSymbolArena(rank.ratedTier) : RankSymbol(rank.tier),
      tierStr: isArena ? rank.ratedTier : rank.tier,
      leaguePoints: isArena
        ? Number.parseInt(rank.ratedRating, 10)
        : Number.parseInt(rank.leaguePoints, 10),
      wins: Number.parseInt(rank.wins, 10),
      losses: Number.parseInt(rank.losses, 10),
      rank: rank.division,
      rankRoman: ((romanNumeral) => {
        if (isArena) return 0;
        switch (romanNumeral.toUpperCase()) {
          case "I":
            return 1;
          case "II":
            return 2;
          case "III":
            return 3;
          case "IV":
            return 4;
          default:
            return "";
        }
      })(typeof rank.division === "string" ? rank.division : ""),
    };
  });

  const validRanks = mappedRanks.filter((queue) => {
    const games = queue.wins + queue.losses;
    return queue.tier || queue.leaguePoints || games;
  });

  const currSeason = getCurrentSeason();

  for (const rank of validRanks) {
    // BE does not support arena enums for this mutation
    if (rank.queue === QUEUE_SYMBOLS.arena) continue;

    if (rank.tier && rank.rank !== "") {
      const season = rank.season || currSeason;

      API.PLAYER.postRankUpdate({
        summonerId: rank.id,
        gameName: account?.riotAccount?.gameName,
        tagLine: account?.riotAccount?.tagLine,
        region: region.toUpperCase(),
        rank: rank.rankRoman,
        tier: rank.tierStr,
        leaguePoints: rank.leaguePoints,
        wins: rank.wins,
        losses: rank.losses,
        season,
        queue: rank.queueType,
      });
      API.PLAYER.postRankUpdateElixir_DEPRECATE_SOON({
        summonerId: rank.id,
        gameName: account?.riotAccount?.gameName,
        tagLine: account?.riotAccount?.tagLine,
        region: region.toUpperCase(),
        rank: rank.rankRoman,
        tier: rank.tierStr,
        leaguePoints: rank.leaguePoints,
        wins: rank.wins,
        losses: rank.losses,
        season,
        queue: rank.queueType,
      });
    }
  }

  validRanks[isPersistent] = Date.now() + 1000 * 60 * 60 * 24 * 7;
  writeState.lol.localSummonerRanks[derivedId] = validRanks;
}

export async function updateCurrentSummoner(summoner) {
  const derivedId = readState.volatile.currentSummoner;
  if (!derivedId) return;

  let profile = readState.lol.profiles[derivedId];
  if (
    !profile ||
    (profile.summonerName === summoner.internalName &&
      profile.summonerLevel === summoner.summonerLevel &&
      profile.profileIconId === summoner.profileIconId)
  )
    return;

  profile = clone(profile);
  profile.summonerName = summoner.internalName;
  profile.summonerLevel = summoner.summonerLevel;
  profile.profileIconId = summoner.profileIconId;

  await getData(
    { data: { leagueProfile: profile } },
    LoLSummoner,
    ["lol", "profiles", derivedId],
    { mergeFn: deepMerge, skipSafetyCheck: true },
  );
}

// Defaults false
export function getLoadingOverlayMinimized() {
  return !!readState.settings.lol.minimizeLoadingScreen;
}

export function updateLoadingOverlayMinimized(value) {
  writeSettings(["lol", "minimizeLoadingScreen"], value);
  return value;
}

export function getPreferredPostmatchComparisonRank() {
  return readState.settings.lol.preferredPostmatchComparisonRank;
}

export function updatePreferredPostmatchComparisonRank(value) {
  const rank = RANK_SYMBOL_TO_STR[value];
  if (!rank) return;
  writeSettings(["lol", "preferredPostmatchComparisonRank"], value);
  writeOverlaySetting(
    GAME_SYMBOL_LOL,
    ["csBenchmarks", "leagueDivision"],
    rank.key,
  );
  writeOverlaySetting(
    GAME_SYMBOL_LOL,
    ["junglePathingBenchmarks", "leagueDivision"],
    rank.key,
  );
  return value;
}

export function loadLocalBans() {
  return readData(["lol", "localPlayerBans"]);
}

export function updateSummonerProfile(region, gameName, tagLine, profile) {
  if (!profile) return;
  const derivedId = getDerivedId(region, null, gameName, tagLine);
  writeState.lol.profiles[derivedId] = profile;
}

export function updateLocalPlayerBans(bannedChampion, role, amt = 1) {
  if (!bannedChampion || Number(bannedChampion) < 1) return;

  const banCounts = clone(readState.lol.localPlayerBans);

  // We want to increment ban counts in two places
  // specific to role, and overall/total so we have
  // options to fall back to/fill in secondarily
  if (!banCounts.all) banCounts.all = {};
  if (!banCounts.all[bannedChampion]) banCounts.all[bannedChampion] = 0;

  if (amt > 0) {
    banCounts.all[bannedChampion] += amt;
  }

  if (role) {
    const roleSymbol = RoleSymbol(role);
    const roleString = ROLE_SYMBOL_TO_STR[roleSymbol]?.gql;
    if (!banCounts[roleString]) banCounts[roleString] = {};

    if (!banCounts[roleString][bannedChampion]) {
      banCounts[roleString][bannedChampion] = 0;
    }

    if (amt > 0) {
      banCounts[roleString][bannedChampion] += amt;
    }
  }

  banCounts[isPersistent] = true;

  writeState.lol.localPlayerBans = banCounts;
}

export function removeLocalPlayerBans(championsToRemove = []) {
  if (!championsToRemove.length) return;

  const banCounts = clone(readState.lol.localPlayerBans);

  // Remove the desired champion IDs from all roles
  for (const id of championsToRemove) {
    for (const role in banCounts) {
      banCounts[role][id] = 0;
    }
  }

  banCounts[isPersistent] = true;

  writeState.lol.localPlayerBans = banCounts;
}

export function updateChatIdentity(identity) {
  setVolatileKV("lolChatIdentity", identity);
}
